<script setup>
import { ref, toRef, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'next/button/Button.vue';
import ConditionRow from 'dashboard/components-next/filter/ConditionRow.vue';
import { useCommercialFilterTypes } from './commercialFilterTypes';

const props = defineProps({
  appliedFilters: { type: Array, default: () => [] },
  // Campo de UTM → valores já usados nas negociações (opções do filtro).
  utmValues: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['apply']);

const { t } = useI18n();
const { filterTypes } = useCommercialFilterTypes(toRef(props, 'utmValues'));
const conditionsRef = useTemplateRef('conditionsRef');

const newRow = type => ({
  attributeKey: type.attributeKey,
  filterOperator: 'equal_to',
  values: [],
  queryOperator: 'and',
});

const draft = ref(
  props.appliedFilters.length
    ? JSON.parse(JSON.stringify(props.appliedFilters))
    : [newRow(filterTypes.value[0])]
);

// Cada campo só pode aparecer uma vez: some das opções dos outros filtros.
const typesFor = index =>
  filterTypes.value.filter(
    type =>
      type.attributeKey === draft.value[index].attributeKey ||
      !draft.value.some(row => row.attributeKey === type.attributeKey)
  );

const unusedType = () =>
  filterTypes.value.find(
    type => !draft.value.some(row => row.attributeKey === type.attributeKey)
  );

const addFilter = () => {
  const type = unusedType();
  if (type) draft.value.push(newRow(type));
};

const removeFilter = index => {
  draft.value.splice(index, 1);
  if (!draft.value.length) draft.value.push(newRow(filterTypes.value[0]));
};

const clearFilters = () => emit('apply', []);

const applyFilters = () => {
  if (!conditionsRef.value.every(condition => condition.validate())) return;
  emit('apply', JSON.parse(JSON.stringify(draft.value)));
};
</script>

<template>
  <div
    class="z-40 w-full max-w-3xl overflow-visible border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg rounded-xl p-6 grid gap-6 lg:w-[750px]"
  >
    <h3 class="text-base font-medium leading-6 text-n-slate-12">
      {{ t('DASHBOARD.COMERCIAL.FILTER.TITLE') }}
    </h3>
    <ul class="grid gap-4 list-none">
      <ConditionRow
        v-for="(filter, index) in draft"
        :key="`${filter.attributeKey}-${index}`"
        ref="conditionsRef"
        v-model:attribute-key="filter.attributeKey"
        v-model:filter-operator="filter.filterOperator"
        v-model:values="filter.values"
        :filter-types="typesFor(index)"
        searchable-attributes
        @remove="removeFilter(index)"
      />
    </ul>
    <div class="flex justify-between gap-2">
      <Button
        sm
        ghost
        blue
        class="flex-shrink-0"
        :disabled="!unusedType()"
        @click="addFilter"
      >
        {{ t('DASHBOARD.COMERCIAL.FILTER.ADD') }}
      </Button>
      <div class="flex flex-shrink-0 gap-2">
        <Button sm faded slate @click="clearFilters">
          {{ t('DASHBOARD.COMERCIAL.FILTER.CLEAR') }}
        </Button>
        <Button sm solid blue @click="applyFilters">
          {{ t('DASHBOARD.COMERCIAL.FILTER.APPLY') }}
        </Button>
      </div>
    </div>
  </div>
</template>
